<template>
  <div class="planner-sheet-deenway">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Planner Grid</h4>
        <b-badge
          v-show="!loading && !errorMessage"
          variant="light-success"
          class="ml-2"
        >
          {{ tableRows.length }} Lessons
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading planner grid...</p>
    </div>

    <div v-else-if="errorMessage" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ errorMessage }}</h4>
    </div>

    <div v-else class="print-page">
      <div class="sheet-header">
        <h1 class="school-name">{{ schoolName }}</h1>
        <h2 class="sheet-title">{{ reportTitle }}</h2>
        <p v-if="sessionName" class="sheet-meta">Session: {{ sessionName }}</p>
      </div>

      <div v-if="!tableRows.length" class="empty-state">
        No planner details found.
      </div>

      <div v-else class="lesson-list">
        <section
          v-for="(row, rowIndex) in tableRows"
          :key="row.key"
          class="lesson-card"
        >
          <div class="lesson-card-head">
            <span class="lesson-index">Lesson {{ rowIndex + 1 }}</span>
            <div class="lesson-meta-grid">
              <div class="meta-item">
                <span class="meta-label">Strand / Concept</span>
                <span class="meta-value" dir="auto">{{
                  row.concept || "—"
                }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">Main Topic</span>
                <span class="meta-value" dir="auto">{{
                  row.topic || "—"
                }}</span>
              </div>
              <div class="meta-item meta-item--full">
                <span class="meta-label">Sub Topic</span>
                <div class="meta-value" dir="auto">
                  <template v-if="row.subtopicLines.length">
                    <div
                      v-for="(line, idx) in row.subtopicLines"
                      :key="`${row.key}-sub-${idx}`"
                      class="content-line"
                    >
                      {{ line }}
                    </div>
                  </template>
                  <span v-else>{{ row.subtopic || "—" }}</span>
                </div>
              </div>
            </div>
          </div>

          <table class="detail-table">
            <tbody>
              <tr
                v-for="header in plannerHeaders"
                :key="`${row.key}-${header.id}`"
              >
                <th class="detail-label">{{ header.header }}</th>
                <td class="detail-value" dir="auto">
                  <template v-if="getHeaderLines(row, header.id).length">
                    <div
                      v-for="(line, idx) in getHeaderLines(row, header.id)"
                      :key="`${row.key}-${header.id}-${idx}`"
                      class="content-line"
                    >
                      {{ line }}
                    </div>
                  </template>
                  <span v-else class="muted">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BSpinner } from "bootstrap-vue";
import { mapActions } from "vuex";

export default {
  name: "PlannerSheetDeenway",
  components: {
    BBadge,
    BSpinner,
  },
  data() {
    return {
      loading: true,
      errorMessage: "",
      sheetId: 0,
      campusId: 0,
      db: "",
      plannerSheet: {},
      plannerHeaders: [],
      concepts: [],
      topics: [],
      subtopics: [],
      classes: [],
      subjects: [],
      sessions: [],
      gradeName: "",
      subjectName: "",
      sessionName: "",
      schoolName: "The Deenway School",
      gridRows: [],
      plannerDetails: [],
      originalPageTitle: "",
      autoPrint: false,
    };
  },
  computed: {
    reportTitle() {
      const grade = this.gradeName || "—";
      const subject = this.subjectName || "—";
      return `Planner Grid for ${grade} ${subject}`;
    },
    tableRows() {
      const rows = Array.isArray(this.gridRows) ? this.gridRows : [];
      if (!rows.length) return [];

      return [...rows].sort((a, b) => {
        const c = String(a.concept || "").localeCompare(String(b.concept || ""));
        if (c !== 0) return c;
        const t = String(a.topic || "").localeCompare(String(b.topic || ""));
        if (t !== 0) return t;
        return String(a.subtopic || "").localeCompare(String(b.subtopic || ""));
      });
    },
  },
  async created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    await this.initReport();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  mounted() {
    if (this.autoPrint) {
      this.$nextTick(() => {
        setTimeout(() => this.print(), 500);
      });
    }
  },
  methods: {
    ...mapActions({ get: "get" }),

    async initReport() {
      const query = this.$route.query || {};
      this.sheetId = Number(query.id) || 0;
      this.db = query.db || this.$store.state.userData.db;
      this.campusId = Number(query.cID || this.$store.state.userData.cId);
      this.autoPrint = query.autoPrint === "1";
      this.gradeName = query.className || "";
      this.subjectName = query.subjectName || "";
      this.sessionName = query.sessionName || "";
      this.schoolName =
        query.campusName ||
        this.$store.state.userData?.campusName ||
        this.$store.state.userData?.organizationName ||
        "The Deenway School";

      if (!this.sheetId) {
        this.errorMessage = "Planner sheet ID is missing.";
        this.loading = false;
        return;
      }

      try {
        await Promise.all([
          this.loadPlannerHeaders(),
          this.loadReferenceData(),
          this.loadSheetData(),
        ]);
        this.buildGridRows();
        this.resolveMetaLabels();
      } catch (error) {
        console.error("Planner sheet Deenway report error:", error);
        this.errorMessage = "Failed to load planner sheet report.";
      } finally {
        this.loading = false;
      }
    },

    async loadPlannerHeaders() {
      const res = await this.get({
        url: `${this.$store.state.domain}PlannerHeaders?db=${this.db}&cId=${this.campusId}`,
        token: this.$store.state.userData.token,
      });
      this.plannerHeaders = this.normalizeList(res).map((h) => ({
        id: h.id,
        header: h.header || h.name || `Header ${h.id}`,
      }));
    },

    async loadReferenceData() {
      const token = this.$store.state.userData.token;
      const domain = this.$store.state.domain;

      const [
        conceptsRes,
        topicsRes,
        subtopicsRes,
        classesRes,
        subjectsRes,
        sessionsRes,
        campusesRes,
      ] = await Promise.all([
        this.get({
          url: `${domain}PlannerConcepts/GetByPagination?db=${this.db}&campusID=${this.campusId}&page=1&pageSize=1000`,
          token,
        }),
        this.get({
          url: `${domain}PlannerMainTopics/GetByPagination?db=${this.db}&campusID=${this.campusId}&page=1&pageSize=1000`,
          token,
        }),
        this.get({
          url: `${domain}PlannerSubTopics/GetByPagination?db=${this.db}&campusID=${this.campusId}&page=1&pageSize=1000`,
          token,
        }),
        this.get({
          url: `${domain}myclasses/LoadClasses?db=${this.db}&cId=${this.campusId}&dID=0`,
          token,
        }),
        this.get({
          url: `${domain}Subjects?db=${this.db}&cId=${this.campusId}`,
          token,
        }),
        this.get({
          url: `${domain}Sessions?db=${this.db}&cID=${this.campusId}`,
          token,
        }),
        this.get({
          url: `${domain}campuses?db=${this.db}&cId=${this.campusId}`,
          token,
        }),
      ]);

      this.concepts = this.normalizeList(conceptsRes).map((item) => ({
        ...item,
        concept: this.cleanConceptText(item.concept),
      }));
      this.topics = this.normalizeList(topicsRes).map((item) => ({
        id: item.id,
        mainTopic: item.topic || item.mainTopic,
      }));
      this.subtopics = this.normalizeList(subtopicsRes).map((item) => ({
        id: item.id,
        subTopic: item.topic || item.subTopic,
      }));
      this.classes = this.normalizeClasses(classesRes);
      this.subjects = Array.isArray(subjectsRes) ? subjectsRes : [];
      this.sessions = this.normalizeList(sessionsRes);

      const campuses = Array.isArray(campusesRes) ? campusesRes : [];
      const campus = campuses.find(
        (c) => Number(c.id) === Number(this.campusId)
      );
      if (campus) {
        this.schoolName =
          campus.organizationName ||
          campus.name ||
          campus.branch ||
          this.schoolName;
      }
    },

    async loadSheetData() {
      const response = await this.get({
        url: `${this.$store.state.domain}PlannerSheet/LoadSelected/${this.campusId}/${this.sheetId}?db=${this.db}`,
        token: this.$store.state.userData.token,
      });

      if (!response) {
        throw new Error("Empty planner sheet response");
      }

      this.plannerSheet = response.plannerSheet || {};
      this.plannerDetails = response.plannerSheetDetails || [];
    },

    buildGridRows() {
      const details = Array.isArray(this.plannerDetails)
        ? this.plannerDetails
        : [];
      const rowsMap = {};

      details.forEach((detail) => {
        const conceptID = detail.conceptID;
        const mainTopicID = detail.mainTopicID;
        const subTopicID = detail.subTopicID;
        const headerID = detail.headerID;
        const key = `${conceptID}-${mainTopicID}-${subTopicID}`;

        if (!rowsMap[key]) {
          const concept = this.concepts.find((c) => c.id == conceptID);
          const topic = this.topics.find((t) => t.id == mainTopicID);
          let subtopic = this.subtopics.find((s) => s.id == subTopicID);

          let subtopicText = subtopic?.subTopic || "";
          if (!subtopicText && topic) {
            const related = this.subtopics.find(
              (s) => s.mainTopicID == mainTopicID
            );
            if (related) subtopicText = related.subTopic || "";
          }

          const headers = {};
          this.plannerHeaders.forEach((header) => {
            headers[header.id] = "";
          });

          rowsMap[key] = {
            key,
            concept: concept?.concept || "",
            topic: topic?.mainTopic || "",
            subtopic: subtopicText,
            headers,
          };
        }

        const value = this.processContentForDisplay(detail.remarks || "");
        if (headerID != null && headerID !== "") {
          const matchedHeader = this.plannerHeaders.find(
            (h) => h.id == headerID
          );
          const storeKey = matchedHeader ? matchedHeader.id : headerID;
          rowsMap[key].headers[storeKey] = value;
        }
      });

      const missingHeaderIds = [
        ...new Set(
          details
            .map((d) => d.headerID)
            .filter(
              (id) =>
                id != null &&
                id !== "" &&
                !this.plannerHeaders.some((h) => h.id == id)
            )
        ),
      ];
      missingHeaderIds.forEach((id) => {
        this.plannerHeaders.push({ id, header: `Header ${id}` });
        Object.values(rowsMap).forEach((row) => {
          if (row.headers[id] === undefined) row.headers[id] = "";
        });
      });

      this.gridRows = Object.values(rowsMap)
        .map((row) => {
          const subtopicLines = String(row.subtopic || "")
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean);

          return {
            ...row,
            subtopicLines,
          };
        })
        .filter(
          (row) =>
            row.concept ||
            row.topic ||
            row.subtopic ||
            Object.values(row.headers || {}).some((v) => String(v || "").trim())
        );
    },

    getHeaderValue(row, headerId) {
      if (!row || !row.headers) return "";
      if (row.headers[headerId] != null) return row.headers[headerId];
      const matchKey = Object.keys(row.headers).find((k) => k == headerId);
      return matchKey != null ? row.headers[matchKey] : "";
    },

    getHeaderLines(row, headerId) {
      const value = String(this.getHeaderValue(row, headerId) || "").trim();
      if (!value) return [];
      return value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    },

    resolveMetaLabels() {
      const sheet = this.plannerSheet || {};

      if (sheet.classID) {
        const cls = this.classes.find((item) => item.cls?.id === sheet.classID);
        if (cls?.cls?.name) this.gradeName = cls.cls.name;
      }

      if (sheet.subjectID) {
        const subject = this.subjects.find(
          (item) => item.id === sheet.subjectID
        );
        if (subject?.subject) this.subjectName = subject.subject;
      }

      if (sheet.sessionID) {
        const session = this.sessions.find(
          (item) => item.id === sheet.sessionID
        );
        if (session?.session) this.sessionName = session.session;
      }
    },

    normalizeList(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    },

    normalizeClasses(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    },

    cleanConceptText(text) {
      if (!text) return "";
      return String(text)
        .replace(/^(VIII|IX|VII|VI|V|IV|III|II|I)\s*-\s*/i, "")
        .replace(/^(Class\s+\d+|Grade\s+\d+)\s*-\s*/i, "")
        .replace(/^(\d+)\s*-\s*/, "")
        .trim();
    },

    stripHtmlTags(text) {
      if (!text) return "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = text;
      return (tempDiv.textContent || tempDiv.innerText || "").trim();
    },

    processContentForDisplay(content) {
      if (!content) return "";
      const value = String(content).trim();
      if (!value.includes("<")) return value;
      return this.stripHtmlTags(value);
    },

    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.planner-sheet-deenway {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
  box-sizing: border-box;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem 1.75rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.2rem;
  }
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
}

.print-page {
  background: #fff;
  padding: 2rem 2.25rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  max-width: 980px;
  margin: 0 auto;
  box-sizing: border-box;
}

.sheet-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.9rem;
  border-bottom: 3px solid #111;
}

.school-name {
  margin: 0 0 0.35rem;
  font-size: 1.55rem;
  font-weight: 800;
  color: #000;
}

.sheet-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
}

.sheet-meta {
  margin: 0.4rem 0 0;
  font-size: 0.9rem;
  color: #636e72;
}

.lesson-list {
  display: flex;
  flex-direction: column;
  gap: 1.35rem;
}

.lesson-card {
  border: 1px solid #dfe3ea;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 10px rgba(67, 89, 113, 0.06);
}

.lesson-card-head {
  background: linear-gradient(180deg, #f8f7ff 0%, #f3f0ff 100%);
  border-bottom: 1px solid #e4deff;
  padding: 1rem 1.15rem;
}

.lesson-index {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.75rem;
  padding: 0.2rem 0.7rem;
  border-radius: 999px;
  background: #7367f0;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.lesson-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem 1rem;
}

.meta-item {
  min-width: 0;
}

.meta-item--full {
  grid-column: 1 / -1;
}

.meta-label {
  display: block;
  margin-bottom: 0.2rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c5ce7;
}

.meta-value {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: #1f1635;
  line-height: 1.45;
  word-break: break-word;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.detail-table tr:nth-child(even) td,
.detail-table tr:nth-child(even) th {
  background: #fafbfd;
}

.detail-label {
  width: 28%;
  padding: 0.7rem 0.9rem;
  border-top: 1px solid #edf0f5;
  border-right: 1px solid #edf0f5;
  vertical-align: top;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  color: #3d3560;
  background: #f8f9fc;
  line-height: 1.35;
  word-break: break-word;
}

.detail-value {
  width: 72%;
  padding: 0.7rem 0.95rem;
  border-top: 1px solid #edf0f5;
  vertical-align: top;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #2d3436;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.content-line + .content-line {
  margin-top: 0.2rem;
}

.muted {
  color: #9ca3af;
}

@media (max-width: 767.98px) {
  .planner-sheet-deenway {
    padding: 1rem;
  }

  .print-page {
    padding: 1.25rem 1rem 1.5rem;
  }

  .lesson-meta-grid {
    grid-template-columns: 1fr;
  }

  .detail-label,
  .detail-value {
    display: block;
    width: 100%;
  }

  .detail-label {
    border-right: none;
    border-bottom: 1px dashed #e4e7ec;
    padding-bottom: 0.35rem;
  }

  .detail-value {
    padding-top: 0.35rem;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .planner-sheet-deenway {
    background: #fff !important;
    padding: 0 !important;
    min-height: 0 !important;
  }

  .print-page {
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .sheet-header {
    margin-bottom: 0.75rem !important;
    padding-bottom: 0.4rem !important;
    page-break-after: avoid;
  }

  .school-name {
    font-size: 14pt !important;
  }

  .sheet-title {
    font-size: 11pt !important;
  }

  .lesson-list {
    gap: 0.75rem !important;
  }

  .lesson-card {
    box-shadow: none !important;
    border: 1px solid #222 !important;
    border-radius: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .lesson-card-head {
    background: #f3f4f6 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .lesson-index {
    background: #444 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .detail-label {
    background: #f7f7f7 !important;
    font-size: 8.5pt !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .detail-value {
    font-size: 9pt !important;
  }

  .detail-table tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  html,
  body {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }

  #app,
  .content-wrapper,
  .content-area-wrapper,
  .container,
  .router-view,
  .layout-full {
    min-height: 0 !important;
    height: auto !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
}
</style>
